import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";

/**
 * Quantities for size sets (user, 2026-10-30): every size of each set with a
 * Qty and Price box. Only the sizes given a quantity go on the product; the
 * rest are left out.
 *
 * Opened after a catalog product with size sets is looked up, and by the
 * "Add Size Set" button once a set is picked.
 *
 * Tab goes down every Qty box first, then down every Price box; Enter adds
 * the sizes (user, 2026-11-02).
 *
 * sets      [{ id, name, items: [{ id, size: { name } }] }] (POST /sales/size-sets)
 * title     heading, "Size Set Quantities" by default
 * onApply([{ size, quantity, price }]), onHide
 */
export default function SizeSetQtyModal({ show, sets = [], title = "Size Set Quantities", onApply, onHide }) {
  const [values, setValues] = useState({});

  useEffect(() => {
    if (show) setValues({});
  }, [show]);

  const keyOf = (set, item) => `${set.id}:${item.id}`;
  const setValue = (key, field, value) =>
    setValues((prev) => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  // One price for every size of the set.
  const setPriceForAll = (set, price) =>
    setValues((prev) => {
      const next = { ...prev };
      (set.items || []).forEach((item) => {
        const key = keyOf(set, item);
        next[key] = { ...next[key], price };
      });
      return next;
    });

  const entries = sets.flatMap((set) =>
    (set.items || []).map((item) => ({
      size: item.size?.name,
      ...values[keyOf(set, item)],
    })),
  );
  const filled = entries.filter((e) => parseInt(e.quantity, 10) > 0).length;

  // tabIndex: Qty boxes 1..n, Price boxes n+1..2n, across all sets.
  const order = new Map(
    sets.flatMap((set) => (set.items || []).map((item) => keyOf(set, item))).map((key, i) => [key, i + 1]),
  );
  const total = order.size;

  const apply = () => {
    if (filled) onApply?.(entries);
  };
  const onEnter = (e) => {
    if (e.key === "Enter" && e.target.tagName === "INPUT") {
      e.preventDefault();
      apply();
    }
  };

  return (
    <Modal
      size="lg"
      show={show}
      onHide={onHide}
      scrollable
      contentClassName="border border-2 shadow-lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body onKeyDown={onEnter}>
        <p className="text-muted small mb-2">
          Enter a quantity for the sizes this order needs. Sizes left empty are
          not added.
        </p>
        {sets.map((set) => (
          <div key={set.id} className="border rounded mb-3">
            <div className="d-flex align-items-center justify-content-between gap-2 bg-light px-2 py-1 border-bottom">
              <span className="fw-bold">{set.name}</span>
              <input
                type="number"
                min="0"
                step="0.01"
                className="form-control form-control-sm w-auto"
                placeholder="Price for all"
                aria-label={`Price for all sizes of ${set.name}`}
                onChange={(e) => setPriceForAll(set, e.target.value)}
              />
            </div>
            {!(set.items || []).length && (
              <div className="text-muted small px-2 py-2">
                No sizes in this set yet. Add them in Utility → Products →
                Sizes → Size Sets.
              </div>
            )}
            <table className="table table-sm mb-0 align-middle">
              <thead>
                <tr>
                  <th className="ps-2">Size</th>
                  <th style={{ width: 140 }}>Qty</th>
                  <th style={{ width: 140 }}>Price</th>
                </tr>
              </thead>
              <tbody>
                {(set.items || []).map((item) => {
                  const key = keyOf(set, item);
                  return (
                    <tr key={key}>
                      <td className="ps-2 fw-semibold">{item.size?.name}</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          className="form-control form-control-sm"
                          tabIndex={order.get(key)}
                          autoFocus={order.get(key) === 1}
                          aria-label={`Quantity ${item.size?.name}`}
                          value={values[key]?.quantity ?? ""}
                          onChange={(e) => setValue(key, "quantity", e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="form-control form-control-sm"
                          tabIndex={total + order.get(key)}
                          aria-label={`Price ${item.size?.name}`}
                          value={values[key]?.price ?? ""}
                          onChange={(e) => setValue(key, "price", e.target.value)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="btn btn-secondary" onClick={onHide}>
          Skip
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!filled}
          onClick={apply}
        >
          Add {filled} size{filled === 1 ? "" : "s"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
